import { useEffect, useMemo, useState } from 'react';
import type {
  WealthAnalysisViewModelV2,
  WealthGainOverTimeViewModelV2,
} from 'api/models.v2';
import {
  fetchWealthAnalysis,
  fetchWealthGainsOverTime,
} from 'store/redux/homeowner-accounts/actions';
import { useAppDispatch } from 'store/redux/use-app-dispatch';

const MAINTENANCE_EXPENSE_TITLE = 'Maintenance & Reconditioning';

export type GainOverTime = WealthGainOverTimeViewModelV2 & {
  totalExpenses?: number;
};

type Request = () => unknown;

type Fetched<T> = {
  request: Request;
  data?: T;
};

function useHomeRead<T>(request?: Request) {
  const dispatch = useAppDispatch();
  const [fetched, setFetched] = useState<Fetched<T>>();

  useEffect(() => {
    if (!request) return;

    let isCancelled = false;

    async function read(currentRequest: Request) {
      try {
        const data: T = await dispatch(currentRequest());

        if (!isCancelled) setFetched({ request: currentRequest, data });
      } catch (_e) {
        if (!isCancelled) setFetched({ request: currentRequest });
      }
    }

    read(request);

    return () => {
      isCancelled = true;
    };
  }, [dispatch, request]);

  return fetched && fetched.request === request ? fetched.data : undefined;
}

export function useWealthAnalysis(homeId?: string | null) {
  const year = new Date().getFullYear();
  const request = useMemo(
    () => (homeId ? () => fetchWealthAnalysis(homeId, year) : undefined),
    [homeId, year],
  );
  const wealthAnalysis = useHomeRead<WealthAnalysisViewModelV2>(request);

  return { wealthAnalysis, year };
}

export function useGainsOverTime(homeId: string, enabled: boolean) {
  const request = useMemo(
    () => (enabled ? () => fetchWealthGainsOverTime(homeId) : undefined),
    [enabled, homeId],
  );

  return useHomeRead<GainOverTime[]>(request);
}

export function getMaintenanceExpense(
  wealthAnalysis?: WealthAnalysisViewModelV2,
) {
  const expense = wealthAnalysis?.belongsViewModel?.expenses?.find(
    (item) => item.title === MAINTENANCE_EXPENSE_TITLE,
  );

  if (!expense) return undefined;

  return {
    budget: Math.abs(expense.amount),
    spent: Math.abs(expense.historicalAmount),
  };
}
